import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { UserDisplay } from "@/components/user-display"
import { X } from "lucide-react"
import { useState } from "react"
import type { UserSummary, TeamMembershipRole } from "@/types"
import type { TeamWithMembers } from "@/components/team-card"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  team: TeamWithMembers
  allUsers: UserSummary[]
}

export function TeamMembersDialog({ open, onOpenChange, team, allUsers }: Props) {
  const [addingUserId, setAddingUserId] = useState("")
  const [addingRole, setAddingRole] = useState<TeamMembershipRole>("member")

  const memberships = team.team_memberships || []
  const memberUserIds = new Set(memberships.map((m) => String(m.user.id)))
  const availableUsers = allUsers.filter((u) => !memberUserIds.has(String(u.id)))

  const buildMembershipsPayload = (
    currentMemberships: typeof memberships,
    overrides?: { addUserId?: string; addRole?: TeamMembershipRole; removeUserId?: string; changeUserId?: string; changeRole?: TeamMembershipRole },
  ) => {
    let result = currentMemberships.map((m) => ({
      user_id: String(m.user.id),
      role: overrides?.changeUserId === String(m.user.id) ? (overrides.changeRole || m.role) : m.role,
    }))

    if (overrides?.removeUserId) {
      result = result.filter((m) => m.user_id !== overrides.removeUserId)
    }

    if (overrides?.addUserId) {
      result.push({ user_id: overrides.addUserId, role: overrides.addRole || "member" })
    }

    return result
  }

  const handleAdd = () => {
    if (!addingUserId) return
    router.patch(
      `/teams/${team.id}`,
      { memberships: buildMembershipsPayload(memberships, { addUserId: addingUserId, addRole: addingRole }) },
      {
        preserveScroll: true,
        onSuccess: () => {
          setAddingUserId("")
          setAddingRole("member")
          onOpenChange(false)
        },
      },
    )
  }

  const handleRemove = (userId: string) => {
    router.patch(
      `/teams/${team.id}`,
      { memberships: buildMembershipsPayload(memberships, { removeUserId: userId }) },
      { preserveScroll: true, onSuccess: () => onOpenChange(false) },
    )
  }

  const handleRoleChange = (userId: string, role: TeamMembershipRole) => {
    router.patch(
      `/teams/${team.id}`,
      { memberships: buildMembershipsPayload(memberships, { changeUserId: userId, changeRole: role }) },
      { preserveScroll: true, onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Membres de {team.name}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Current members */}
          <div className="space-y-2">
            {memberships.length > 0 ? (
              memberships.map((membership) => (
                <div key={membership.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <UserDisplay user={membership.user} size="sm" />
                  <div className="flex items-center gap-2">
                    <Select
                      value={membership.role}
                      onValueChange={(v) => handleRoleChange(String(membership.user.id), v as TeamMembershipRole)}
                    >
                      <SelectTrigger className="w-[120px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Membre</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon-sm" onClick={() => handleRemove(String(membership.user.id))}>
                      <X className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">Aucun membre</p>
            )}
          </div>

          {/* Add member */}
          {availableUsers.length > 0 && (
            <div className="flex gap-2">
              <Select value={addingUserId} onValueChange={setAddingUserId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Ajouter un membre..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={addingRole} onValueChange={(v) => setAddingRole(v as TeamMembershipRole)}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Membre</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAdd} disabled={!addingUserId}>
                Ajouter
              </Button>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
